import { motion } from "framer-motion";
import { Users, User, Phone, CheckCircle2 } from "lucide-react";
import { formatPhoneOnInput, validateGhanaPhoneNumber } from "../../../utils/formValidators";
import { useState } from "react";

export default function Step2ContactDetails({ formData, setFormData, errors }) {
  const [phoneValidation, setPhoneValidation] = useState({
    contactPersonTelephone: null,
    contactPersonCellNumber: null,
    secondContactPersonTelephone: null,
    secondContactPersonCellNumber: null,
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhoneChange = (field, value) => {
    const formatted = formatPhoneOnInput(value);
    handleChange(field, formatted);
    const validation = validateGhanaPhoneNumber(formatted.replace(/\s/g, ""));
    setPhoneValidation((prev) => ({ ...prev, [field]: validation.isValid ? "valid" : null }));
  };

  const err = (f) => (errors?.[f] ? "border-red-300 bg-red-50" : "border-gray-300 hover:border-gray-400");
  const valid = (f) => (phoneValidation[f] === "valid" ? "border-green-300 bg-green-50" : "");

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="p-8 bg-white rounded-xl border border-gray-200 shadow-sm"
    >
      <div className="flex gap-3 items-center mb-8">
        <div className="flex justify-center items-center w-10 h-10 text-white bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-md">
          <Users size={20} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Contact Details Information</h2>
      </div>

      <div className="mb-8">
        <div className="flex gap-3 items-center mb-6">
          <div className="flex justify-center items-center w-8 h-8 text-purple-600 bg-purple-50 rounded-lg">
            <User size={16} />
          </div>
          <h3 className="text-lg font-semibold text-gray-800">Contact Person Details</h3>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-6">
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-700">Name of Contact Person <span className="text-red-500">*</span></label>
              <input type="text" value={formData.contactPersonName || ""} onChange={(e) => handleChange("contactPersonName", e.target.value)} className={"w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 " + err("contactPersonName")} placeholder="Enter contact person name" />
              {errors?.contactPersonName && <p className="mt-1 text-sm text-red-500">{errors.contactPersonName}</p>}
            </div>
            <div>
              <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700"><Phone size={16} className="text-purple-600" /> Telephone Number <span className="text-red-500">*</span></label>
              <div className="relative">
                <input type="tel" value={formData.contactPersonTelephone || ""} onChange={(e) => handlePhoneChange("contactPersonTelephone", e.target.value)} className={"w-full px-4 py-3 pr-10 rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 " + (valid("contactPersonTelephone") || err("contactPersonTelephone"))} placeholder="0241234567 or 233241234567" />
                {phoneValidation.contactPersonTelephone === "valid" && <CheckCircle2 className="absolute top-3.5 right-3 text-green-500" size={20} />}
              </div>
              {errors?.contactPersonTelephone && <p className="mt-1 text-sm text-red-500">{errors.contactPersonTelephone}</p>}
            </div>
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-700">Email Address <span className="text-red-500">*</span></label>
              <input type="email" value={formData.contactPersonEmail || ""} onChange={(e) => handleChange("contactPersonEmail", e.target.value)} className={"w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 " + err("contactPersonEmail")} placeholder="contact@example.com" />
              {errors?.contactPersonEmail && <p className="mt-1 text-sm text-red-500">{errors.contactPersonEmail}</p>}
            </div>
          </div>
          <div className="space-y-6">
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-700">Designation <span className="text-red-500">*</span></label>
              <input type="text" value={formData.contactPersonDesignation || ""} onChange={(e) => handleChange("contactPersonDesignation", e.target.value)} className={"w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 " + err("contactPersonDesignation")} placeholder="Enter designation" />
              {errors?.contactPersonDesignation && <p className="mt-1 text-sm text-red-500">{errors.contactPersonDesignation}</p>}
            </div>
            <div>
              <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700"><Phone size={16} className="text-purple-600" /> Cell Number <span className="text-red-500">*</span></label>
              <div className="relative">
                <input type="tel" value={formData.contactPersonCellNumber || ""} onChange={(e) => handlePhoneChange("contactPersonCellNumber", e.target.value)} className={"w-full px-4 py-3 pr-10 rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 " + (valid("contactPersonCellNumber") || err("contactPersonCellNumber"))} placeholder="0241234567 or 233241234567" />
                {phoneValidation.contactPersonCellNumber === "valid" && <CheckCircle2 className="absolute top-3.5 right-3 text-green-500" size={20} />}
              </div>
              {errors?.contactPersonCellNumber && <p className="mt-1 text-sm text-red-500">{errors.contactPersonCellNumber}</p>}
            </div>
          </div>
        </div>
      </div>

      <div className="my-8 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />

      <div>
        <div className="flex gap-3 items-center mb-6">
          <div className="flex justify-center items-center w-8 h-8 text-purple-600 bg-purple-50 rounded-lg">
            <User size={16} />
          </div>
          <h3 className="text-lg font-semibold text-gray-800">Second Contact Person Details</h3>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-6">
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-700">Name of Second Contact Person <span className="text-red-500">*</span></label>
              <input type="text" value={formData.secondContactPersonName || ""} onChange={(e) => handleChange("secondContactPersonName", e.target.value)} className={"w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 " + err("secondContactPersonName")} placeholder="Enter second contact person name" />
              {errors?.secondContactPersonName && <p className="mt-1 text-sm text-red-500">{errors.secondContactPersonName}</p>}
            </div>
            <div>
              <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700"><Phone size={16} className="text-purple-600" /> Telephone Number <span className="text-red-500">*</span></label>
              <div className="relative">
                <input type="tel" value={formData.secondContactPersonTelephone || ""} onChange={(e) => handlePhoneChange("secondContactPersonTelephone", e.target.value)} className={"w-full px-4 py-3 pr-10 rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 " + (valid("secondContactPersonTelephone") || err("secondContactPersonTelephone"))} placeholder="0241234567 or 233241234567" />
                {phoneValidation.secondContactPersonTelephone === "valid" && <CheckCircle2 className="absolute top-3.5 right-3 text-green-500" size={20} />}
              </div>
              {errors?.secondContactPersonTelephone && <p className="mt-1 text-sm text-red-500">{errors.secondContactPersonTelephone}</p>}
            </div>
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-700">Email Address <span className="text-red-500">*</span></label>
              <input type="email" value={formData.secondContactPersonEmail || ""} onChange={(e) => handleChange("secondContactPersonEmail", e.target.value)} className={"w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 " + err("secondContactPersonEmail")} placeholder="contact@example.com" />
              {errors?.secondContactPersonEmail && <p className="mt-1 text-sm text-red-500">{errors.secondContactPersonEmail}</p>}
            </div>
          </div>
          <div className="space-y-6">
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-700">Designation <span className="text-red-500">*</span></label>
              <input type="text" value={formData.secondContactPersonDesignation || ""} onChange={(e) => handleChange("secondContactPersonDesignation", e.target.value)} className={"w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 " + err("secondContactPersonDesignation")} placeholder="Enter designation" />
              {errors?.secondContactPersonDesignation && <p className="mt-1 text-sm text-red-500">{errors.secondContactPersonDesignation}</p>}
            </div>
            <div>
              <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700"><Phone size={16} className="text-purple-600" /> Cell Number <span className="text-red-500">*</span></label>
              <div className="relative">
                <input type="tel" value={formData.secondContactPersonCellNumber || ""} onChange={(e) => handlePhoneChange("secondContactPersonCellNumber", e.target.value)} className={"w-full px-4 py-3 pr-10 rounded-lg border outline-none focus:ring-2 focus:ring-purple-500 " + (valid("secondContactPersonCellNumber") || err("secondContactPersonCellNumber"))} placeholder="0241234567 or 233241234567" />
                {phoneValidation.secondContactPersonCellNumber === "valid" && <CheckCircle2 className="absolute top-3.5 right-3 text-green-500" size={20} />}
              </div>
              {errors?.secondContactPersonCellNumber && <p className="mt-1 text-sm text-red-500">{errors.secondContactPersonCellNumber}</p>}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
